import {isEmpty} from "underscore";

const PKCE_RANDOM_BYTE_LENGTH = 32;
const SALESFORCE_OAUTH_SESSION_PREFIX = "salesforceOAuth";

export const salesforceAuthorizeEndpoint =
  "https://login.salesforce.com/services/oauth2/authorize";

export const createSalesforceAuthorizationUrl = async ({clientId, orgName, redirectUri}) => {
  const codeVerifier = createRandomValue();
  const codeChallenge = await createCodeChallenge(codeVerifier);
  const state = `${encodeText(orgName)}.${createRandomValue()}`;
  clearPendingSalesforceAuthorizations();
  window.sessionStorage.setItem(
    getSalesforceOAuthSessionKey(state),
    JSON.stringify({codeVerifier}),
  );

  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    state,
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });

  return `${salesforceAuthorizeEndpoint}?${params}`;
};

export const consumeSalesforceCodeVerifier = (state) => {
  if (isEmpty(state)) {
    throw new Error("Salesforce authorization state is missing or invalid");
  }

  const sessionKey = getSalesforceOAuthSessionKey(state);
  const storedAuthorization = window.sessionStorage.getItem(sessionKey);
  window.sessionStorage.removeItem(sessionKey);

  try {
    const {codeVerifier} = JSON.parse(storedAuthorization);
    if (isEmpty(codeVerifier)) {
      throw new Error();
    }
    return codeVerifier;
  } catch {
    throw new Error("Salesforce authorization state is missing or invalid");
  }
};

const createRandomValue = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(PKCE_RANDOM_BYTE_LENGTH));
  return encodeBase64Url(bytes);
};

const createCodeChallenge = async (codeVerifier) => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(codeVerifier),
  );
  return encodeBase64Url(new Uint8Array(digest));
};

const encodeText = (value) => encodeBase64Url(new TextEncoder().encode(value));

const encodeBase64Url = (bytes) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");

const getSalesforceOAuthSessionKey = (state) =>
  `${SALESFORCE_OAUTH_SESSION_PREFIX}:${state}`;

const clearPendingSalesforceAuthorizations = () => {
  for (let index = window.sessionStorage.length - 1; index >= 0; --index) {
    const key = window.sessionStorage.key(index);
    if (key?.startsWith(`${SALESFORCE_OAUTH_SESSION_PREFIX}:`)) {
      window.sessionStorage.removeItem(key);
    }
  }
};
